<script setup lang="ts">
import { Expand, LocateFixed, Minus, Plus, Shrink, Workflow } from '@lucide/vue'
import { type Edge, MarkerType, type Node, type NodeMouseEvent, Position, useVueFlow } from '@vue-flow/core'
import { MiniMap } from '@vue-flow/minimap'
import { computed, type HTMLAttributes, nextTick, ref, type SVGAttributes, useId, watch } from 'vue'
import type { TaskRow } from '@/api'
import FlowCanvas from '@/components/FlowCanvas.vue'
import TaskGraphEdge from '@/components/TaskGraphEdge.vue'
import TaskGraphNode from '@/components/TaskGraphNode.vue'
import {
    buildTaskGraph,
    nodeObstacles,
    TASK_GRAPH_NODE_HEIGHT,
    TASK_GRAPH_NODE_WIDTH,
    type TaskGraphNode as TaskGraphNodeData,
} from '@/lib/taskGraph'
import { dataAttributes } from '@/lib/vueFlowAttributes'
import '@vue-flow/minimap/dist/style.css'
import { ZhTooltip } from '@zerohuman/ui'

const props = defineProps<{
    tasks: TaskRow[]
    scope?: 'team' | 'role'
    relatedTasks?: TaskRow[]
}>()
const emit = defineEmits<{ select: [taskId: string]; 'select-role': [roleId: string] }>()
const id = useId()
const { fitView, zoomIn, zoomOut, zoomTo, setCenter, viewport, dimensions, onNodesInitialized } = useVueFlow(id)
const expanded = ref(false)
const showLabels = ref(true)
const hovered = ref<string | null>(null)
const jumpTo = ref('')
const graph = computed(() => buildTaskGraph(props.tasks, { scope: props.scope, relatedTasks: props.relatedTasks }))
const externalCount = computed(() => graph.value.nodes.filter(node => node.offCanvas).length)
const taskCount = computed(() => graph.value.nodes.filter(node => !node.offCanvas).length)
const zoomPercent = computed(() => Math.round(viewport.value.zoom * 100))

const nodes = computed<Node<TaskGraphNodeData>[]>(() =>
    graph.value.nodes.map(node => ({
        id: node.slug,
        type: 'task',
        data: node,
        position: node.position,
        draggable: false,
        connectable: false,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        domAttributes: dataAttributes<HTMLAttributes>(
            node.taskId ? { 'data-task-id': node.taskId } : { 'data-off-canvas': 'true' },
        ),
    })),
)

const edges = computed<Edge[]>(() =>
    graph.value.edges.map(edge => {
        const active = hovered.value === edge.source || hovered.value === edge.target
        const color = active ? 'var(--electric-cyan)' : 'var(--muted-grey)'
        const source = graph.value.nodes.find(node => node.slug === edge.source)
        const target = graph.value.nodes.find(node => node.slug === edge.target)
        const left = Math.min(source?.position.x ?? 0, target?.position.x ?? 0)
        const right = Math.max(source?.position.x ?? 0, target?.position.x ?? 0)
        const routeY =
            Math.max(
                ...graph.value.nodes
                    .filter(node => node.position.x >= left && node.position.x <= right)
                    .map(node => node.position.y + TASK_GRAPH_NODE_HEIGHT),
            ) +
            44 +
            edge.returnLane * 28
        return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: 'workflow',
            data: {
                ...edge,
                showLabel: showLabels.value,
                routeY,
                muted: Boolean(hovered.value && !active),
                active,
                obstacles: nodeObstacles(graph.value.nodes),
            },
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color },
            class: hovered.value && !active ? 'task-edge-muted' : undefined,
            style: {
                stroke: color,
                strokeWidth: active ? 2 : 1.5,
                ...(edge.dangling ? { strokeDasharray: '5 5' } : {}),
            },
            domAttributes: {
                'data-edge-source': edge.source,
                'data-edge-target': edge.target,
                ...(edge.dangling ? { 'data-dangling': 'true' } : {}),
            } as SVGAttributes,
        }
    }),
)

function fitCanvas() {
    void fitView({ padding: 0.2, maxZoom: 1, duration: 250 })
}

// Node dimensions are only available after Vue Flow has mounted and measured the cards.
onNodesInitialized(fitCanvas)
watch(
    () => graph.value.nodes.map(node => `${node.slug}:${node.position.x}:${node.position.y}`).join('|'),
    async () => {
        await nextTick()
        fitCanvas()
    },
)
// ResizeObserver has committed the new canvas dimensions before fitting an expanded view.
watch(() => [dimensions.value.width, dimensions.value.height], fitCanvas)

function focusTask() {
    const node = graph.value.nodes.find(candidate => candidate.slug === jumpTo.value)
    if (!node) return
    void setCenter(node.position.x + TASK_GRAPH_NODE_WIDTH / 2, node.position.y + TASK_GRAPH_NODE_HEIGHT / 2, {
        zoom: 1,
        duration: 300,
    })
    jumpTo.value = ''
}

function selectNode(data: TaskGraphNodeData | undefined) {
    if (props.scope === 'role' && data?.offCanvas && data.roleId) {
        emit('select-role', data.roleId)
    } else if (data?.taskId) {
        emit('select', data.taskId)
    }
}

function onNodeClick({ node }: NodeMouseEvent) {
    selectNode(node.data as TaskGraphNodeData | undefined)
}
</script>

<template>
	<section class="task-graph" :class="{ 'task-graph-expanded': expanded }" aria-label="Task workflow" @keydown.esc="expanded = false">
		<header class="task-graph-toolbar">
			<div class="task-graph-summary">
				<Workflow :size="16" aria-hidden="true" />
				<span><strong>{{ taskCount }} tasks</strong><span class="task-graph-count"> · {{ graph.edges.length }} connections<span v-if="externalCount"> · {{ externalCount }} outside this view</span></span></span>
			</div>
			<div class="task-graph-options">
				<label class="task-graph-label-toggle"><input v-model="showLabels" type="checkbox" /> Labels</label>
				<select v-model="jumpTo" aria-label="Find a task in this workflow" @change="focusTask">
					<option value="">Find a task…</option>
					<option v-for="node in graph.nodes" :key="node.slug" :value="node.slug">{{ node.name || node.slug }}</option>
				</select>
				<ZhTooltip :text="expanded ? 'Collapse workflow (Esc)' : 'Expand workflow'">
					<button type="button" :aria-label="expanded ? 'Collapse workflow' : 'Expand workflow'" @click="expanded = !expanded">
						<Shrink v-if="expanded" :size="16" /><Expand v-else :size="16" />
					</button>
				</ZhTooltip>
			</div>
		</header>
		<div class="task-graph-canvas">
			<FlowCanvas :id="id" :nodes="nodes" :edges="edges" @node-click="onNodeClick" @node-mouse-enter="hovered = $event.node.id" @node-mouse-leave="hovered = null">
				<MiniMap v-if="graph.nodes.length > 6" :width="150" :height="90" :pannable="true" :zoomable="true" :node-border-radius="6" node-color="var(--electric-cyan)" mask-color="rgba(120, 120, 140, 0.10)" />
				<template #node-task="nodeProps"><ZhTooltip :text="[nodeProps.data.name || nodeProps.data.slug, nodeProps.data.goal].filter(Boolean).join('\n')"><TaskGraphNode :data="nodeProps.data" :navigation="scope === 'role' ? 'role' : 'task'" @activate="selectNode(nodeProps.data)" /></ZhTooltip></template>
				<template #edge-workflow="edgeProps"><TaskGraphEdge v-bind="edgeProps" /></template>
			</FlowCanvas>
			<div class="task-graph-controls" aria-label="Workflow navigation">
				<ZhTooltip text="Zoom out"><button type="button" aria-label="Zoom out" @click="zoomOut({ duration: 200 })"><Minus :size="16" /></button></ZhTooltip>
				<ZhTooltip text="View at 100%"><button type="button" aria-label="Reset zoom to 100%" @click="zoomTo(1, { duration: 200 })"><output aria-label="Zoom level">{{ zoomPercent }}%</output></button></ZhTooltip>
				<ZhTooltip text="Zoom in"><button type="button" aria-label="Zoom in" @click="zoomIn({ duration: 200 })"><Plus :size="16" /></button></ZhTooltip>
				<span class="task-graph-divider" />
				<ZhTooltip text="Fit workflow to view"><button type="button" aria-label="Fit workflow to view" @click="fitCanvas"><LocateFixed :size="16" /><span>Fit</span></button></ZhTooltip>
			</div>
			<p class="task-graph-hint">Drag to pan · Pinch to zoom · {{ scope === 'role' ? 'Click a task or follow a role handoff' : 'Click a task to open' }}</p>
		</div>
	</section>
</template>

<style scoped>
.task-graph {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    background: var(--background);
}
.task-graph-expanded {
    position: fixed;
    inset: 16px;
    z-index: 60;
    width: auto;
    height: auto;
    border: 1px solid var(--border);
    border-radius: 0;
    overflow: hidden;
    box-shadow: 0 0 0 100vmax #0008, 0 24px 80px #0003;
}
.task-graph-toolbar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border);
    background: var(--card);
}
.task-graph-summary, .task-graph-options {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
}
.task-graph-summary > svg {
    color: var(--electric-cyan);
}
.task-graph-summary strong {
    font-weight: 600;
}
.task-graph-count {
    color: var(--muted-foreground);
}
.task-graph-options select {
    width: 170px;
    border: 1px solid var(--border);
    border-radius: 0;
    padding: 6px 8px;
    background: var(--background);
    color: var(--foreground);
}
.task-graph-label-toggle {
    display: flex;
    gap: 5px;
    align-items: center;
    color: var(--muted-foreground);
}
.task-graph-label-toggle input {
    accent-color: var(--electric-cyan);
}
.task-graph button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: 0;
    height: 30px;
    min-width: 30px;
    padding: 0 7px;
    cursor: pointer;
    color: var(--foreground);
}
.task-graph button:hover {
    background: var(--muted);
}
.task-graph button:focus-visible, .task-graph select:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}
.task-graph-canvas {
    position: relative;
    flex: 1;
    min-height: 0;
    background: color-mix(in srgb, var(--muted) 45%, var(--background));
}
.task-graph-controls {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 2px;
    bottom: 18px;
    left: 18px;
    padding: 4px;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 0;
    box-shadow: 0 2px 8px #00000008;
}
.task-graph-controls output {
    width: 44px;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    text-align: center;
    color: var(--muted-foreground);
}
.task-graph-controls button span {
    font-size: 12px;
}
.task-graph-divider {
    height: 18px;
    width: 1px;
    margin: 0 4px;
    background: var(--border);
}
.task-graph-hint {
    position: absolute;
    bottom: 28px;
    left: 235px;
    font-size: 11px;
    color: var(--muted-foreground);
    pointer-events: none;
}
.task-graph :deep(.vue-flow__edge) {
    transition: opacity .15s;
}
.task-graph :deep(.task-edge-muted) {
    opacity: .18;
}
.task-graph :deep(.vue-flow__minimap) {
    border: 1px solid var(--border);
    border-radius: 0;
    overflow: hidden;
    background: var(--card);
}
.task-graph :deep(.vue-flow__edge-path) {
    fill: none;
}

@media (max-width: 760px) {
    .task-graph-hint, .task-graph-count { display: none; }
    .task-graph-toolbar { padding: 10px; }
    .task-graph-expanded { inset: 6px; }
}
</style>
